export const cartPageEventsConfig = {
    parentSelector: '#main-cart',
    events: {
        // Cart | click on product image (CSV Line 153)
        cartPageProductImageClick: {
            selector: '.cart-item__image', // Target the image element itself
            event: {
                event: 'cart',
                component: 'cart',
                element: 'image',
                action: 'click',
                trigger: 'click',
            }
        },

        // Cart | click on product name (CSV Line 154)
        cartPageProductNameClick: {
            selector: 'a.cart-item__name.h4.font-heading-bold', // Updated selector
            event: {
                event: 'cart',
                component: 'cart',
                element: 'link',
                action: 'click', // Action remains 'click' conceptually for GTM
                trigger: 'pointerdown', // Changed trigger to pointerdown
                getDynamicData: (element) => { // element is the <a> tag
                    return {
                        product_name: element.textContent?.trim() || 'Unknown Product',
                        link_url: element.href,
                    };
                }
            }
        },

        // Cart | focus in number field (CSV Line 155)
        cartPageQuantityFocus: {
            selector: '.cart-item .quantity__input', // Target quantity input within a cart item
            event: {
                event: 'cart',
                element: 'number', // CSV uses 'number' here
                field: 'quantity',
                action: 'focus',
                trigger: 'focus',
                getDynamicData: (element) => {
                    const cartItem = element.closest('.cart-item');
                    // Try the specific selector first, fall back to the general one
                    const productNameElement = cartItem?.querySelector('a.cart-item__name.h4.font-heading-bold') || cartItem?.querySelector('.cart-item__name a'); 
                    const componentName = productNameElement ? productNameElement.textContent.trim() : 'Unknown Product';

                    return { component: componentName }; // Only component needed for focus
                }
            }
        },

        // Cart | input in number field (CSV Line 156)
        cartPageQuantityInput: {
            selector: '.cart-item .quantity__input',
            event: {
                event: 'cart',
                element: 'number',
                field: 'quantity',
                action: 'input',
                trigger: 'input', // Debounced by datalayer.js
                getDynamicData: (element) => {
                    const cartItem = element.closest('.cart-item');
                    // Try the specific selector first, fall back to the general one
                    const productNameElement = cartItem?.querySelector('a.cart-item__name.h4.font-heading-bold') || cartItem?.querySelector('.cart-item__name a'); 
                    const componentName = productNameElement ? productNameElement.textContent.trim() : 'Unknown Product';

                    return {
                        component: componentName,
                        input_value: element.value, // Add input_value for input event
                    };
                }
            }
        },

        // Cart | click on arrow up (CSV Line 157)
        cartPageQuantityUp: {
            selector: '.cart-item .quantity__button[name="plus"]', // Target plus button
            event: {
                event: 'cart',
                element: 'chevron',
                field: 'quantity',
                action: 'click',
                action_value: 'up',
                trigger: 'click',
                getDynamicData: (element) => {
                    const cartItem = element.closest('.cart-item');
                    // Try the specific selector first, fall back to the general one
                    const productNameElement = cartItem?.querySelector('a.cart-item__name.h4.font-heading-bold') || cartItem?.querySelector('.cart-item__name a'); 
                    const componentName = productNameElement ? productNameElement.textContent.trim() : 'Unknown Product';

                    return { component: componentName }; // Only component needed for click
                }
            }
        },

        // Cart | click on arrow down (CSV Line 158)
        cartPageQuantityDown: {
            selector: '.cart-item .quantity__button[name="minus"]', // Target minus button
            event: {
                event: 'cart',
                element: 'chevron',
                field: 'quantity',
                action: 'click',
                action_value: 'down',
                trigger: 'click',
                getDynamicData: (element) => {
                    const cartItem = element.closest('.cart-item');
                    // Try the specific selector first, fall back to the general one
                    const productNameElement = cartItem?.querySelector('a.cart-item__name.h4.font-heading-bold') || cartItem?.querySelector('.cart-item__name a'); 
                    const componentName = productNameElement ? productNameElement.textContent.trim() : 'Unknown Product';

                    return { component: componentName }; // Only component needed for click
                }
            }
        },

        // Cart | remove product (bin next/underneath) (CSV Lines 159/160)
        cartPageRemoveItem: {
            // General selector for remove buttons within a cart item
            selector: 'a.link.align-center.justify-right.por.tap-area-icon', 
            event: {
                event: 'cart',
                element: 'icon',
                action: 'click',
                action_value: 'remove',
                trigger: 'click',
                getDynamicData: (element) => {
                    const cartItem = element.closest('.cart-item');
                     // Find product name using robust selector logic
                    const productNameElement = cartItem?.querySelector('a.cart-item__name.h4.font-heading-bold') || cartItem?.querySelector('.cart-item__name a');
                    const componentName = productNameElement ? productNameElement.textContent.trim() : 'Unknown Product';
                    // CSV specifies component:product_name, and field:quantity for line 159
                    // but no field for line 160. We'll include component consistently.
                    return { component: componentName }; 
                }
            }
        },

        // Cart | accordion open/close (note / shipping) (CSV Lines 161/162)
        cartPageAccordionToggle: {
            selector: 'div.summary-accordion__plus-minus-wrapper.flex.justify-center.align-center', // Selector targets the icon wrapper
            event: {
                event: 'cart',
                // component: 'cart', // Component determined dynamically
                element: 'accordion',
                action: 'click', // Changed from open/close to simple click/toggle
                trigger: 'click',
                getDynamicData: (element) => { // element is the clicked DIV
                    const summaryElement = element.closest('summary'); // Go up to the parent summary element
                    if (!summaryElement) {
                        console.warn('[Datalayer] Could not find parent summary for accordion toggle:', element);
                        return { component: 'cart', click_text: 'Unknown Accordion' }; 
                    }

                    // Attempt to find a more specific element holding the text, or use the summary's text
                    // Adjust '.accordion__title' if your title is in a different span/class
                    const titleElement = summaryElement.querySelector('.accordion__title, span'); 
                    const clickText = titleElement ? titleElement.textContent?.trim() : summaryElement.textContent?.trim();

                    // Map text to component
                    let componentName = 'cart'; // Default
                    if (clickText?.includes('instructies')) componentName = 'note';
                    if (clickText?.includes('Shipping') || clickText?.includes('shipping')) componentName = 'shipping_calculator';
                    
                    return {
                        component: componentName,
                        // Use the extracted title text for click_text
                        click_text: clickText || 'Unknown Accordion Title' 
                    };
                }
            }
        },

        // Cart | focus on note text field (CSV Line 163)
        cartPageNoteFocus: {
            selector: 'textarea[name="note"], #Cart-note', // Standard selectors for cart notes
            event: {
                event: 'cart',
                component: 'note',
                element: 'text_field',
                action: 'focus',
                trigger: 'focus'
                // No dynamic data needed for focus itself
            }
        },

        // Cart | input in note text field (CSV Line 164)
        cartPageNoteInput: {
            selector: 'textarea[name="note"], #Cart-note', // Standard selectors for cart notes
            event: {
                event: 'cart',
                component: 'note',
                element: 'text_field',
                action: 'input',
                trigger: 'input', // Debounced by datalayer.js
                getDynamicData: (element) => {
                    return {
                        input_value: element.value // Capture the current value
                    };
                }
            }
        },

        // == Shipping Calculator (within #main-cart) ==

        // Cart | shipping dropdown select (CSV Line 167) - Adapt from modal
        cartPageShippingDropdownSelect: {
            selector: 'select#AddressCountryCalculator.select__select', // Target select elements within #main-cart
            event: {
                event: 'cart',
                component: 'shipping_calculator',
                element: 'dropdown',
                action: 'select',
                trigger: 'change', // Use 'change' event for select elements
                getDynamicData: (element) => {
                    const label = document.querySelector(`label[for="${element.id}"]`);
                    const selectedOption = element.options[element.selectedIndex];
                    return {
                        field_name: label ? label.textContent.trim() : (element.id.includes('Country') ? 'Land/regio' : 'Provincie'), // Default based on ID
                        select_value: selectedOption ? selectedOption.text : '' // Get text of the selected option
                    };
                }
            }
        },

        // Cart | shipping focus in text field (CSV Line 168) - Adapt from modal
        cartPageShippingFocus: {
            selector: 'input#AddressZipCalculator.field__input', // Target the postcode input within #main-cart
            event: {
                event: 'cart',
                component: 'shipping_calculator',
                element: 'text_field',
                action: 'focus',
                trigger: 'focus',
                getDynamicData: (element) => {
                    const label = document.querySelector(`label[for="${element.id}"]`);
                    return {
                        field_name: label ? label.textContent.trim() : 'Postcode' // Default field name
                    };
                }
            }
        },

        // Cart | shipping input in text field (CSV Line 169) - Adapt from modal
        cartPageShippingInput: {
            selector: 'input#AddressZipCalculator.field__input', // Target the postcode input within #main-cart
            event: {
                event: 'cart',
                component: 'shipping_calculator',
                element: 'text_field',
                action: 'input',
                trigger: 'input', // Debounced by datalayer.js
                getDynamicData: (element) => {
                    const label = document.querySelector(`label[for="${element.id}"]`);
                    return {
                        field_name: label ? label.textContent.trim() : 'Postcode',
                        input_value: element.value // Capture the input value
                    };
                }
            }
        },

        // Cart | shipping click on cta-button (CSV Line 171) - Adapt from modal
        cartPageShippingCalculate: {
             // More specific selector for the button within the shipping calculator form
            selector: '#main-cart button.button.button--secondary', 
            event: {
                event: 'cart',
                component: 'shipping_calculator',
                element: 'cta_button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                     // Verify this is the correct button if multiple exist
                     if (!element.textContent?.trim().toLowerCase().includes('calculate') && !element.textContent?.trim().toLowerCase().includes('berekenen')) {
                         return null; // Suppress if not the calculate button
                     }
                    return {
                        // Use button text, default to 'Calculate'
                        click_text: element.textContent?.trim() || 'Calculate' 
                    };
                }
            }
        },

        // Cart | click on link in text (CSV Line 172) - Adapt from modal
        cartPageGenericLinkClick: {
            selector: '#main-cart a[href]', // Target any link with an href inside #main-cart
            event: {
                event: 'cart',
                component: 'cart', // Keep component generic for these links
                element: 'link',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    // Check if this link belongs to a more specific tracked interaction
                    if (element.closest('.cart-item, .cart__checkout-button, .cart__disclosure summary, .button--remove, cart-remove-button')) {
                         return null; // Suppress event if part of a more specific component/action
                    }
                    
                    // Ensure the link has meaningful text
                    const linkText = element.textContent.trim();
                    if (!linkText) {
                        return null; // Don't track clicks on links without text (e.g., image links already handled)
                    }

                    return {
                        link_text: linkText,
                        link_url: element.href
                    };
                }
            }
        },

        // Cart | click on cta-button (afrekenen /payment methods) (CSV Line 173)
        cartPageCheckout: {
            selector: '#main-cart .cart__checkout-button button, #main-cart button[name="checkout"]', // Common checkout button selectors
            event: {
                event: 'cart',
                component: 'cart',
                element: 'cta_button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    return {
                        click_text: element.textContent?.trim() || 'Afrekenen' // Default or actual text
                    };
                }
            }
        },

        // == Recommendations Slider Events Moved to cartRecommendationsEventsConfig.js ==

    },
}; 